/**
 * A custom element that navigates to a sibling product's URL when a size is selected.
 *
 * @extends HTMLElement
 */
class SizePicker extends HTMLElement {
  connectedCallback() {
    this.#syncCurrentSelection();
    this.addEventListener('change', this.#handleChange);
    window.addEventListener('pageshow', this.#handlePageShow);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  /**
   * @param {Event} event
   */
  #handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    const target = event.target;
    const productUrl = target?.dataset.productUrl;

    if (productUrl && this.#normalizePath(productUrl) !== this.#normalizePath(window.location.href)) {
      window.location.assign(productUrl);
    }
  };

  #handlePageShow = () => {
    this.#syncCurrentSelection();
  };

  #syncCurrentSelection() {
    const inputs = /** @type {HTMLInputElement[]} */ (
      Array.from(this.querySelectorAll('input[type="radio"][data-product-url]'))
    );

    if (!inputs.length) return;

    const currentHandle = this.dataset.currentProductHandle;
    const currentPath = this.#normalizePath(window.location.href);
    const currentInput =
      inputs.find((input) => input.dataset.productHandle === currentHandle) ||
      inputs.find((input) => this.#normalizePath(input.dataset.productUrl || '') === currentPath) ||
      inputs.find((input) => input.dataset.currentChecked === 'true');

    for (const input of inputs) {
      const isCurrent = input === currentInput;

      input.checked = isCurrent;
      input.defaultChecked = isCurrent;
      input.dataset.currentChecked = String(isCurrent);

      if (isCurrent) {
        input.setAttribute('checked', '');
      } else {
        input.removeAttribute('checked');
      }
    }
  }

  /**
   * @param {string} url
   * @returns {string}
   */
  #normalizePath(url) {
    try {
      const pathname = new URL(url, window.location.origin).pathname.replace(/\/+$/, '');
      return pathname || '/';
    } catch {
      return url.replace(/\/+$/, '') || '/';
    }
  }
}

if (!customElements.get('size-picker')) {
  customElements.define('size-picker', SizePicker);
}
